<template>
  <main>


    <!-- The user is authenticated -->
    <div v-if="authStore.isAuthenticated" class="mt-3">

      <!-- No organisations are linked to the user's profile -->
      <div v-if="organisations !== null && organisations.length === 0">
        <div class="alert alert-warning" role="alert">
          Your profile is not yet linked to an organisation.
        </div>
      </div>

      <!-- Organisations are linked to the user's profile -->
      <div v-for="organisation in organisations" :key="organisation.id">
        <div class="d-grid gap-2 col-12 mx-auto">
          <button class="btn btn-primary mb-3" @click="navigateToOrganisation(organisation.slug)">{{
              organisation.name
            }}
          </button>
        </div>
        <div class="d-grid gap-2 col-12 mx-auto"
             v-if="organisation && adminOrganisations.some(adminOrg => adminOrg.code === organisation.code)">
          <button class="btn btn-outline-primary mb-3" @click="navigateToOrganisationAdmin(organisation.slug)">{{
              organisation.name
            }} - Admin
          </button>
        </div>
        <div class="d-grid gap-2 col-12 mx-auto"
             v-if="organisation && adminOrganisations.some(adminOrg => adminOrg.code === organisation.code)">
          <button class="btn btn-outline-primary mb-3" @click="navigateToDownloadData(organisation.slug)">{{
              organisation.name
            }} - Download Data
          </button>
        </div>
      </div>

      <!-- The user has not selected a role for an organisation -->
      <div v-if="organisationsWithoutSelectedRole.length > 0">
        <div class="alert alert-danger" role="alert">
          <div class="row">
            <div class="col-auto"><i class="fa-solid fa-hand-point-right"></i></div>
            <div class="col">
              <p v-for="organisation in organisationsWithoutSelectedRole" :key="organisation.id">
                Choose at least one role to get started with {{ organisation.name }}
              </p>
              <router-link to="/account" class="btn btn-primary">Update your account</router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- The user has not selected favourite co-workers or has not toggled the no-preferred-co-workers switch -->
      <div
          v-if="authStore && authStore.user && authStore.user.favourite_co_workers.length === 0 && !authStore.user.no_preferred_co_workers ">
        <div class="alert alert-danger" role="alert">
          <div class="row">
            <div class="col-auto">
              <i class="fa-solid fa-hand-point-right"></i>
            </div>
            <div class="col">
              <p>Specify your favourite co-workers, or ... turn on the "No preferred co-workers" switch if you
                appreciate
                them
                all!</p>
              <router-link to="/account" class="btn btn-primary">Update your account</router-link>
            </div>
          </div>
        </div>
      </div>


      <div>
        <h1 v-if="userShifts.length > 0">Upcoming events</h1>
        <div class="row row-cols-1 row-cols-md-3 g-4 mb-4">
          <div class="col" v-for="userShift in userShifts" :key="userShift.id">
            <div class="card">
              <h2 class="card-header">{{ userShift.shift.event.name }}</h2>
              <div class="card-body">
                <dl>
                  <dt>Shift</dt>
                  <dd>{{ userShift.shift.name }}</dd>
                  <dt>Location</dt>
                  <dd>{{ userShift.shift.location.name }}</dd>
                  <dt>From</dt>
                  <dd>{{ formatLocalTime(userShift.shift.start) }}</dd>
                  <dt>To</dt>
                  <dd>{{ formatLocalTime(userShift.shift.end) }}</dd>
                </dl>
              </div>
              <div
                  :class="['card-footer text-white text-center',
                  { 'bg-warning': userShift.status === 'pending' },
                  { 'bg-danger': userShift.status === 'waiting_list' },
                  { 'bg-success': userShift.status === 'confirmed' }]">
                <dd>{{ userShift.status_display }}</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Goodies -->
      <div v-if="userGoodies.length > 0">
        <h1>Goodies</h1>
        <div class="row row-cols-1 row-cols-md-3 g-4 mb-4">
          <div class="col" v-for="userGoodie in userGoodies" :key="userGoodie.id">
            <div class="card">
              <h2 class="card-header">{{ userGoodie.goodie.name }}</h2>
              <div class="card-body">
                <dl>
                  <dt>Description</dt>
                  <dd>{{ userGoodie.goodie.description }}</dd>
                </dl>
              </div>
              <div
                  :class="['card-footer text-white text-center',
                  { 'bg-warning': userGoodie.status === 'pending' },
                  { 'bg-danger': userGoodie.status === 'waiting_list' },
                  { 'bg-success': userGoodie.status === 'confirmed' }]">
                <dd>{{ userGoodie.status_display }}</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- The user is not yet authenticated -->
    <div v-else>
      <h1 class="text-center">Welcome to MyCrew</h1>
      <blockquote class="blockquote text-center">
        <p>Your gateway to effortless organization and seamless teamwork.</p>
      </blockquote>
      <p class="text-center">Let's get started!</p>
      <div class="row">
        <div class="col-sm-6 mb-3 mb-sm-0">
          <div class="card text-center">
            <div class="card-header">Already part of the crew?</div>
            <div class="card-body">
              <p class="card-text">Login to dive back in.</p>
              <router-link to="/login" class="btn btn-primary">Login</router-link>
            </div>
          </div>
        </div>
        <div class="col-sm-6">
          <div class="card text-center">
            <div class="card-header">New here?</div>
            <div class="card-body">
              <p class="card-text">Smash that Register button and join the adventure!</p>
              <router-link to="/register" class="btn btn-secondary">Register</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import {ref, onMounted} from 'vue'
import {useAuthStore} from '@/store/auth.js'
import {useRouter} from 'vue-router'
import {fetchAdminOrganisations, fetchOrganisations, fetchUserShifts} from '@/api.js'
import dayjs from "dayjs";
import {API_BASE_URL} from "@/config";


function getOrganisationsWithoutSelectedRole(organisations, userRoles) {
  const organisationsWithoutSelectedRole = [];
  organisations.forEach(organisation => {
    if (organisation.roles && organisation.roles.length > 0) {
      const hasUserRole = organisation.roles.some(role => Array.isArray(userRoles) && userRoles.some(userRole => userRole.role_id === role.id)
      );
      if (!hasUserRole) {
        organisationsWithoutSelectedRole.push(organisation);
      }
    }
  });
  return organisationsWithoutSelectedRole;
}

export default {
  setup() {
    const authStore = useAuthStore()
    // const route = useRoute()
    const router = useRouter()
    const organisations = ref(null)
    const adminOrganisations = ref([])
    const user = ref({})
    const userShifts = ref([])
    const organisationsWithoutSelectedRole = ref([])
    const userGoodies = ref([])


    onMounted(async () => {
      await authStore.fetchUser()
      user.value = authStore.user
      if (authStore.isAuthenticated) {
        organisations.value = await fetchOrganisations()
        adminOrganisations.value = await fetchAdminOrganisations()
        userShifts.value = await fetchUserShifts()
        organisationsWithoutSelectedRole.value = getOrganisationsWithoutSelectedRole(organisations.value, user.value.user_roles)
        await fetchUserGoodies()
      }
    })


    const navigateToOrganisation = (slug) => {
      router.push({name: 'organisation', params: {slug}})
    }

    const navigateToOrganisationAdmin = (slug) => {
      console.log("Navigating to organisation admin with slug: ", slug)
      router.push({name: 'organisation-admin', params: {slug}})
    }

    const navigateToDownloadData = (slug) => {
      console.log("Navigating to organisation-download-data data with slug: ", slug)
      router.push({name: 'organisation-download-data', params: {slug}})
    }

    const fetchUserGoodies = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user-goodies`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          userGoodies.value = data;
        }
      } catch (error) {
        console.error('Error fetching user goodies:', error);
      }
    }

    return {
      authStore,
      router,
      organisations,
      adminOrganisations,
      user,
      userShifts,
      navigateToOrganisation,
      navigateToOrganisationAdmin,
      navigateToDownloadData,
      organisationsWithoutSelectedRole,
      userGoodies,
    }
  },
  methods: {
    async logout() {
      try {
        await this.authStore.logout(this.$router)
      } catch (error) {
        console.error(error)
      }
    },
    formatLocalTime(dateString) {
      return dayjs(dateString).format('ddd DD/MM/YYYY HH:mm');
    },


  },
}


</script>


